<template>
  <div @click="tryClose"
       data-notify="container"
       class="alert"
       :class="[{'alert-with-icon': icon || showClose}, verticalAlign, horizontalAlign, alertType]"
       role="alert"
       :style="customPosition"
       data-notify-position="top-center">
    <button
      v-if="showClose"
      type="button"
      aria-hidden="true"
      class="close"
      data-notify="dismiss"
      @click="close">
      <i class="nc-icon nc-simple-remove"></i>
    </button>

    <span v-if="icon" data-notify="icon" :class="['alert-icon', icon]"></span>
    <span data-notify="message">
      <div v-if="title" class="title"><b>{{title}}<br/></b></div>
      <div v-if="message">{{ message }}</div>
      <b v-if="count > 1" class="notification-count">&nbsp;(×{{ count }})</b>
      <content-render v-if="!message && component" :component="component"></content-render>
    </span>
  </div>
</template>
<script>
import { defineComponent, h } from 'vue';
export default defineComponent({
  name: 'notification',
  components: {
    contentRender: {
      props: ['component'],
      render() {
        return h(this.component)
      }
    }
  },
  props: {
    message: String,
    title: String,
    icon: String,
    verticalAlign: {
      type: String,
      default: 'top',
      validator: value => {
        let acceptedValues = ['top', 'bottom'];
        return acceptedValues.indexOf(value) !== -1;
      }
    },
    horizontalAlign: {
      type: String,
      default: 'right',
      validator: value => {
        let acceptedValues = ['left', 'center', 'right'];
        return acceptedValues.indexOf(value) !== -1;
      }
    },
    type: {
      type: String,
      default: 'info',
      validator: value => {
        let acceptedValues = [
          'info',
          'primary',
          'danger',
          'warning',
          'success'
        ];
        return acceptedValues.indexOf(value) !== -1;
      }
    },
    timeout: {
      type: Number,
      default: 5000,
      validator: value => {
        return value >= 0;
      }
    },
    timestamp: {
      type: Date,
      default: () => new Date()
    },
    count: {
      type: Number,
      default: 1
    },
    component: {
      type: [Object, Function]
    },
    showClose: {
      type: Boolean,
      default: true
    },
    closeOnClick: {
      type: Boolean,
      default: true
    },
    clickHandler: Function
  },
  data() {
    return {
      elmHeight: 0
    };
  },
  computed: {
    hasIcon() {
      return this.icon && this.icon.length > 0;
    },
    alertType() {
      return `alert-${this.type}`;
    },
    customPosition() {
      let initialMargin = this.isMobile ? 10 : 20;
      let alertHeight = this.elmHeight + (this.isMobile ? 5 : 10);
      let sameAlertsCount = this.$notifications.state.filter(alert => {
        return (
          alert.horizontalAlign === this.horizontalAlign &&
          alert.verticalAlign === this.verticalAlign &&
          alert.timestamp <= this.timestamp
        );
      }).length;
      if (this.$notifications.settings.overlap) {
        sameAlertsCount = 1;
      }
      let pixels = (sameAlertsCount - 1) * alertHeight + initialMargin;
      let styles = {};
      if (this.verticalAlign === 'top') {
        styles.top = `${pixels}px`;
      } else {
        styles.bottom = `${pixels}px`;
      }
      return styles;
    },
    isMobile() {
      return window.innerWidth <= 768;
    }
  },
  methods: {
    close() {
      this.$emit('close', this.timestamp);
    },
    tryClose(evt) {
      if (this.clickHandler) {
        this.clickHandler(evt, this);
      }
      if (this.closeOnClick) {
        this.close();
      }
    }
  },
  mounted() {
    this.elmHeight = this.$el.clientHeight;
    if (this.timeout) {
      setTimeout(this.close, this.timeout);
    }
  }
});
</script>
<style lang="scss">
.notifications .alert {
  position: fixed;
  z-index: 10000;
  max-width: calc(100vw - 40px);
  min-width: 280px;
  word-wrap: break-word;
  box-sizing: border-box;
 
  &[data-notify='container'] {
    width: 480px;
    cursor: pointer;
    
    // Mobile responsiveness
    @media (max-width: 768px) {
      width: calc(100vw - 20px);
      max-width: 400px;
      min-width: 280px;
      font-size: 0.9rem;
    }
    
    @media (max-width: 480px) {
      width: calc(100vw - 16px);
      max-width: none;
      min-width: auto;
      font-size: 0.85rem;
      padding: 12px 16px;
    }
    
    @media (max-width: 320px) {
      width: calc(100vw - 12px);
      font-size: 0.8rem;
      padding: 10px 12px;
    }
  }

  // Positioning classes with mobile adjustments
  &.center {
    left: 50%;
    transform: translateX(-50%);
    
    @media (max-width: 768px) {
      left: 50%;
      right: auto;
      margin: 0;
    }
  }
  
  &.left {
    left: 20px;
    
    @media (max-width: 768px) {
      left: 10px;
    }
    
    @media (max-width: 480px) {
      left: 8px;
    }
    
    @media (max-width: 320px) {
      left: 6px;
    }
  }
  
  &.right {
    right: 20px;
    
    @media (max-width: 768px) {
      right: 10px;
    }
    
    @media (max-width: 480px) {
      right: 8px;
    }
    
    @media (max-width: 320px) {
      right: 6px;
    }
  }
  
  // Content styling improvements
  .title {
    margin-bottom: 4px;
    
    @media (max-width: 480px) {
      margin-bottom: 2px;
    }
  }
  
  .close {
    padding: 4px 8px;
    font-size: 1.2rem;
    
    @media (max-width: 480px) {
      padding: 2px 6px;
      font-size: 1rem;
    }
  }
  
  .alert-icon {
    @media (max-width: 480px) {
      font-size: 0.9rem;
    }
  }
  
  // Ensure text doesn't overflow
  [data-notify="message"] {
    overflow-wrap: break-word;
    word-break: break-word;
    hyphens: auto;

    @media (max-width: 480px) {
      line-height: 1.4;
    }
  }

  // Repeat counter shown when identical toasts are deduped (see index.js)
  .notification-count {
    display: inline-block;
    white-space: nowrap;
  }
}

// Special handling for very tall notifications on mobile
@media (max-height: 600px) {
  .notifications .alert {
    max-height: 30vh;
    overflow-y: auto;
    
    &[data-notify='container'] {
      font-size: 0.8rem;
      padding: 8px 12px;
    }
  }
}

// Landscape phone orientation
@media (max-width: 768px) and (orientation: landscape) {
  .notifications .alert {
    max-height: 25vh;
    
    &[data-notify='container'] {
      font-size: 0.85rem;
    }
  }
}

/* ---------- Theme-aware toast surfaces ----------
   Overrides the legacy bootstrap alert palettes with the global App.vue
   tokens, so toasts read correctly in both light and dark themes. */
.notifications .alert {
  background-color: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-left: 4px solid var(--color-accent);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  font-family: 'IBM Plex Sans', sans-serif;

  &[data-notify='container'] {
    padding: 14px 18px;
  }

  .title {
    font-family: 'Sora', sans-serif;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--text-primary);
  }

  [data-notify='message'] {
    color: var(--text-secondary);
    font-size: 0.88rem;
    line-height: 1.45;
  }

  .notification-count {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .close {
    color: var(--text-muted);
    opacity: 1;
    text-shadow: none;
    transition: color 0.15s ease;

    &:hover {
      color: var(--text-primary);
    }
  }

  .alert-icon {
    margin-right: 8px;
  }

  &.alert-primary {
    border-left-color: var(--color-accent);

    .alert-icon { color: var(--color-accent); }
  }

  &.alert-info {
    border-left-color: var(--color-info);

    .alert-icon { color: var(--color-info); }
  }

  &.alert-success {
    border-left-color: var(--color-success);

    .alert-icon { color: var(--color-success); }
  }

  &.alert-warning {
    border-left-color: var(--color-warning);

    .alert-icon { color: var(--color-warning); }
  }

  &.alert-danger {
    border-left-color: var(--color-danger);

    .alert-icon { color: var(--color-danger); }
  }
}
</style>
